import { ContentCard } from "./ContentCard";
import { ContentViewerModal } from "./ContentViewerModal";
import { Star } from "lucide-react";
import { cn } from "../../lib/utils";
import { refreshConversationResourceSignedUrl } from "@/services/conversationResourceService";

interface ResourceItemProps {
  id: string;
  type: "pdf" | "audio" | "video" | "image" | "website" | "document";
  title: string;
  description: string;
  meta?: string;
  isFavorited?: boolean;
  addedDate?: string;
  titleClassName?: string;
  className?: string;
  photo?: string;
  url?: string;
  storagePath?: string;
  onFavoriteClick: (id: string) => void;
}

export const ResourceItem = ({
  id,
  type,
  title,
  description,
  meta,
  isFavorited = false,
  addedDate,
  titleClassName,
  className,
  photo,
  url,
  storagePath,
  onFavoriteClick,
}: ResourceItemProps) => {
  const typeInfo =
    type === "pdf"
      ? `PDF: ${meta ?? "Read"}`
      : type === "document"
      ? `WORD: ${meta ?? "Download"}`
      : type === "video"
      ? `VIDEO: ${meta}`
      : type === "website"
      ? `WEBSITE: ${meta ?? "READ"}`
      : type === "image"
      ? `PRINTABLE: ${meta ?? "1 PAGE"}`
      : `AUDIO: ${meta}`;

  const openWithFreshUrl = async () => {
    const targetUrl =
      storagePath != null
        ? (await refreshConversationResourceSignedUrl(storagePath)) ?? url
        : url;
    if (!targetUrl) return;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFavoriteClick(id);
  };

  const cardContent = (
    <div className={cn("w-full h-full", className)}>
      <ContentCard className="h-full hover:shadow-xl transition-all duration-200 flex flex-col cursor-pointer">
        {/* Photo */}
        <div className="relative mb-4">
          <div className="w-full h-40 sm:h-44 md:h-48 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
            {photo ? (
              <img src={photo} alt={title} className="w-full h-full object-cover object-center" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-sm">No image</span>
              </div>
            )}
          </div>

          {/* Favorite button overlay */}
          <button
            onClick={handleFavoriteClick}
            className={`absolute top-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-sm transition-colors ${
              isFavorited ? "text-yellow-500 hover:text-yellow-600" : "text-gray-400 hover:text-yellow-500"
            }`}
          >
            <Star className={`w-4 h-4 ${isFavorited ? "fill-current" : ""}`} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-2 flex-1 flex flex-col min-h-0 overflow-hidden text-ellipsis">
          <div className="text-xs text-gray-500 uppercase tracking-wide">{typeInfo}</div>

          <h3
            className={`text-sm sm:text-base md:text-lg font-semibold text-bodhi-orange leading-tight ${
              titleClassName || ""
            }`}
          >
            {title}
          </h3>

          <div className="flex flex-1 text-gray-600 text-xs sm:text-sm line-clamp-3 sm:line-clamp-4 ">{description}</div>
        </div>
      </ContentCard>
    </div>
  );

  if (type === "document" && url && url !== "#") {
    return (
      <button type="button" className="w-full h-full text-left" onClick={() => void openWithFreshUrl()}>
        {cardContent}
      </button>
    );
  }

  // If there's a valid URL, wrap in ContentViewerModal
  if (url && url !== "#") {
    return (
      <ContentViewerModal
        url={url}
        title={title}
        type={type === "document" ? "pdf" : type}
        photo={photo}
        storagePath={storagePath}
      >
        {cardContent}
      </ContentViewerModal>
    );
  }

  // For resources without URLs, return the card without modal functionality
  return cardContent;
};
